import React from 'react'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface TopCustomersListProps {
  customers: DashboardMetrics['top_customers']
  loading?: boolean
}

export const TopCustomersList: React.FC<TopCustomersListProps> = ({ customers, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    )
  }

  if (!customers || customers.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-heading mb-4">👥 Top 5 Customers Today</h3>
        <p className="text-muted text-center py-8">No customer data available</p>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-xl font-bold text-heading mb-4">👥 Top 5 Customers Today</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted uppercase">Rank</th>
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted uppercase">Customer</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Orders</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Revenue</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Cancel %</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id} className="border-b border-border">
                <td className="py-3 px-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    customer.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                    customer.rank === 2 ? 'bg-gray-100 text-gray-700' :
                    customer.rank === 3 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {customer.rank === 1 ? '🥇' :
                     customer.rank === 2 ? '🥈' :
                     customer.rank === 3 ? '🥉' :
                     customer.rank}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <p className="font-semibold text-heading">{customer.name}</p>
                  {customer.phone && (
                    <p className="text-xs text-muted">{customer.phone}</p>
                  )}
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-semibold text-heading">{customer.orders}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-semibold text-success-600">{formatCurrency(customer.revenue)}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className={`text-sm font-medium ${
                    customer.cancel_rate > 20 ? 'text-danger-600' :
                    customer.cancel_rate > 10 ? 'text-warning-600' :
                    'text-success-600'
                  }`}>
                    {customer.cancel_rate.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
